import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { useFormContext, UseFormReturn } from 'react-hook-form';
import { Input } from '../../../../components-v2/Input';
import { InputGroupAddon, InputGroupInput, InputGroupV2 } from '../../../../components-v2/InputGroup';
import { TextArea } from '../../../../components-v2/TextArea';
import { THEME } from '../../../../components-v2/Theme/theme';
import { Toggle } from '../../../../components/Toggle';
import { FormFieldWrapper } from '../../shared/components/FormFieldWrapper';
import { EventRegistrationFormData } from '../utils';

const {
	spacing,
	typography: { fontSizes },
} = THEME;

interface TicketCardProps {
	index: number;
	control: UseFormReturn<EventRegistrationFormData>['control'];
	formState: UseFormReturn<EventRegistrationFormData>['formState'];
	onDelete?: () => void;
	isPriceDisabled?: boolean;
}

export const TicketCard: React.FC<TicketCardProps> = ({
	index,
	control,
	formState,
	onDelete,
	isPriceDisabled = false,
}) => {
	const { watch, setValue } = useFormContext<EventRegistrationFormData>();

	// Watch the current values of both toggles
	const hideTicket = watch(`eventTickets.${index}.hideTicket`);
	const showAsSoldOut = watch(`eventTickets.${index}.showAsSoldOut`);

	return (
		<div className={css(styles.ticketCard)}>
			{/* Header with Ticket Tier number and Delete button */}
			<div className={css(styles.cardHeader)}>
				<h3 className={css(styles.cardTitle)}>Ticket Tier #{index + 1}</h3>
				{onDelete && !isPriceDisabled && (
					<button type='button' onClick={onDelete} className={css(styles.deleteButton)}>
						Delete Ticket Tier
					</button>
				)}
			</div>

			{/* Tier Name Input */}
			<FormFieldWrapper
				name={`eventTickets.${index}.name`}
				control={control}
				title='Tier Name'
				description='Add a name for this tier of tickets'
				textWrapperStyles={[styles.textWrapper]}
				containerStyles={[styles.inputContainer]}
				controllerWrapperStyles={[styles.controllerWrapper]}
			>
				{({ value, onChange }) => (
					<>
						<Input
							id={`ticket-name-${index}`}
							type='text'
							value={value || ''}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								onChange(e.target.value);
							}}
							placeholder='Basic Ticket'
						/>
						{formState.errors.eventTickets?.[index]?.name && (
							<p className={css(styles.errorMessage)}>{formState.errors.eventTickets[index].name.message}</p>
						)}
					</>
				)}
			</FormFieldWrapper>

			{/* Description Textarea */}
			<FormFieldWrapper
				name={`eventTickets.${index}.description`}
				control={control}
				title='Description'
				description='Add a description for this tier of tickets'
				textWrapperStyles={[styles.textWrapper]}
				containerStyles={[styles.inputContainer]}
				controllerWrapperStyles={[styles.controllerWrapper]}
			>
				{({ value, onChange }) => (
					<>
						<TextArea
							id={`ticket-description-${index}`}
							value={value || ''}
							styles={[styles.textArea]}
							onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
								onChange(e.target.value);
							}}
							placeholder='With the basic ticket, enjoy the show with our economy seating and obstructed views. Drinks not included.'
							rows={3}
						/>
						{formState.errors.eventTickets?.[index]?.description && (
							<p className={css(styles.errorMessage)}>{formState.errors.eventTickets[index].description.message}</p>
						)}
					</>
				)}
			</FormFieldWrapper>

			{/* Ticket Cost and Max Tickets Row */}
			<div className={css(styles.costMaxRow)}>
				{/* Ticket Cost Input */}
				<FormFieldWrapper
					name={`eventTickets.${index}.unitPrice`}
					control={control}
					title='Ticket Cost'
					description='Set the cost for tickets in this tier'
					containerStyles={[styles.inputContainer]}
					textWrapperStyles={[styles.textWrapper]}
					controllerWrapperStyles={[styles.controllerWrapper, styles.costControllerWrapper]}
				>
					{({ value, onChange }) => (
						<>
							<InputGroupV2>
								<InputGroupInput
									id={`ticket-price-${index}`}
									type='text'
									value={value ? (value / 100).toString() : ''}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
										const val = e.target.value;
										if (val === '') {
											onChange(0);
											return;
										}
										const num = parseFloat(val);
										if (!isNaN(num) && num >= 0) {
											// Convert dollars to cents
											onChange(Math.round(num * 100));
										}
									}}
									disabled={isPriceDisabled}
									placeholder='10'
								/>
								<InputGroupAddon>$</InputGroupAddon>
							</InputGroupV2>
							{formState.errors.eventTickets?.[index]?.unitPrice && (
								<p className={css(styles.errorMessage)}>{formState.errors.eventTickets[index].unitPrice.message}</p>
							)}
						</>
					)}
				</FormFieldWrapper>
			</div>
			<div className={css(styles.costMaxRow)}>
				{/* Max Tickets Input */}
				<FormFieldWrapper
					name={`eventTickets.${index}.maxAllowed`}
					control={control}
					title='Max Tickets'
					description='Set the maximum amount of tickets for this tier'
					containerStyles={[styles.inputContainer]}
					textWrapperStyles={[styles.textWrapperMaxTickets]}
					controllerWrapperStyles={[styles.controllerWrapper, styles.maxControllerWrapper]}
				>
					{({ value, onChange }) => (
						<>
							<Input
								id={`ticket-max-allowed-${index}`}
								type='text'
								value={value?.toString() || ''}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
									const val = e.target.value;
									if (val === '') {
										onChange(null);
										return;
									}
									const num = parseInt(val, 10);
									if (!isNaN(num) && num > 0) {
										onChange(num);
									}
								}}
								placeholder='100'
							/>
							{formState.errors.eventTickets?.[index]?.maxAllowed && (
								<p className={css(styles.errorMessage)}>{formState.errors.eventTickets[index].maxAllowed.message}</p>
							)}
						</>
					)}
				</FormFieldWrapper>
			</div>

			{/* Hide Tier Toggle - Hidden when Sold Out is enabled */}
			{!showAsSoldOut && (
				<FormFieldWrapper
					name={`eventTickets.${index}.hideTicket`}
					control={control}
					title='Hide Tier'
					description='Hide this ticket tier on the event registration page'
				>
					{({ value, onChange }) => (
						<Toggle
							id={`hide-tier-${index}`}
							isOn={value || false}
							uncheckedColor={THEME.colors.border.main}
							onToggleCheckChanged={() => {
								const newValue = !value;
								onChange(newValue);

								// If Hide Tier is enabled, disable Sold Out
								if (newValue) {
									setValue(`eventTickets.${index}.showAsSoldOut`, false);
								}
							}}
						/>
					)}
				</FormFieldWrapper>
			)}

			{/* Sold Out Toggle - Hidden when Hide Tier is enabled */}
			{!hideTicket && (
				<FormFieldWrapper
					name={`eventTickets.${index}.showAsSoldOut`}
					control={control}
					title='Sold Out'
					description='Disable this tier and mark it as Sold Out'
				>
					{({ value, onChange }) => (
						<Toggle
							id={`sold-out-${index}`}
							isOn={value || false}
							uncheckedColor={THEME.colors.border.main}
							onToggleCheckChanged={() => {
								const newValue = !value;
								onChange(newValue);

								// If Sold Out is enabled, disable Hide Tier
								if (newValue) {
									setValue(`eventTickets.${index}.hideTicket`, false);
								}
							}}
						/>
					)}
				</FormFieldWrapper>
			)}
		</div>
	);
};

const styles = StyleSheet.create({
	textWrapper: {
		minWidth: '100%',
		marginBottom: spacing.sm,
	},
	inputContainer: {
		flexDirection: 'column',
		justifyContent: 'flex-start',
		alignItems: 'flex-start',
	},
	controllerWrapper: {
		width: '100%',
		maxWidth: 600,
		flexDirection: 'column',
	},
	textWrapperMaxTickets: {},
	textArea: {
		backgroundColor: THEME.colors.background.light,
	},
	ticketCard: {
		backgroundColor: THEME.colors.background.main,
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: 8,
		padding: spacing.xl,
		marginBottom: spacing.md,
	},
	cardHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: spacing.lg,
	},
	cardTitle: {
		fontSize: fontSizes.lg,
		fontWeight: THEME.typography.fontWeights.bold,
		margin: 0,
		color: THEME.colors.text.main,
	},
	fieldContainer: {
		marginBottom: spacing.md,
		position: 'relative',
	},
	label: {
		display: 'block',
		fontSize: fontSizes.md,
		marginBottom: spacing.xs,
		fontWeight: THEME.typography.fontWeights.standard,
		color: THEME.colors.text.main,
	},
	helperText: {
		display: 'block',
		fontSize: fontSizes.sm,
		color: THEME.colors.text.light,
		marginBottom: spacing.sm,
		marginTop: 0,
		fontWeight: THEME.typography.fontWeights.standard,
	},
	costMaxRow: {
		display: 'flex',
		gap: spacing.md,
		marginBottom: spacing.md,
	},
	costControllerWrapper: {
		maxWidth: 200,
	},
	maxControllerWrapper: {
		maxWidth: 200,
	},
	toggleContainer: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: spacing.md,
		gap: spacing.md,
	},
	toggleContent: {
		flex: 1,
	},
	toggleLabel: {
		display: 'block',
		fontSize: fontSizes.md,
		marginBottom: spacing.xs,
		fontWeight: THEME.typography.fontWeights.standard,
		color: THEME.colors.text.main,
	},
	toggleHelperText: {
		display: 'block',
		fontSize: fontSizes.sm,
		color: THEME.colors.text.light,
		marginTop: 0,
		marginBottom: 0,
		fontWeight: THEME.typography.fontWeights.standard,
	},
	deleteButton: {
		backgroundColor: 'transparent',
		border: 'none',
		color: THEME.colors.danger.main,
		cursor: 'pointer',
		fontSize: fontSizes.sm,
		padding: 0,
		fontWeight: THEME.typography.fontWeights.standard,
		':hover': {
			textDecoration: 'underline',
		},
		':disabled': {
			opacity: 0.5,
			cursor: 'not-allowed',
		},
	},
	errorMessage: {
		color: THEME.colors.danger.main,
		fontSize: fontSizes.xs,
		marginTop: spacing.xs,
		marginBottom: 0,
	},
});
